/* eslint-disable react-hooks/exhaustive-deps */
import React, {useContext, useEffect, useState} from "react"
import {useTranslation} from "react-i18next"
import moment from "moment"
import cx from "classnames"
import {Button, Col, Dropdown, Menu, Row, Spin, Tooltip} from "antd"
import {get, isEmpty, uniq, uniqBy} from "lodash"
import {useHistory} from "react-router-dom"
import {Icon} from "components/Icon"
import {
    checkPermission,
    copyTextToClipboard,
    formatDateOnly,
    getFieldLabel,
    getFullNameOrPreferredName,
    getShortName,
    handleError,
    isFieldActive,
    toastInfo
} from "helpers"
import {studentActivitiesService, studentService, userServiceV3} from "services"
import {useAllEnrollmentTypes, useModel, useMutation, useShowEnrollmentIdField, useTotalScheduleHoursToLda} from "hooks"
import {BaseDepartmentId} from "types/departments"
import {Auth} from "types/auth"
import {stringifyUrl} from "query-string"
import {StudentStatus} from "types/students"
import {Permissions} from "types/permission"
import {AdvisorTitle} from "./parts"
import styles from "./StudentInfo.module.css"
import orderBy from "lodash/orderBy"
import {AcademicsForm, AdmissionsForm, FinancialAidForm, StudentServicesForm} from "./parts/StudentDetailsEdit"
import {Settings} from "types/settings"
import {BaseLoading} from "components"
import {genderPronounOptionsKeyById} from "sections/users/Detail/parts/Personal/parts/GeneralDetail"
import {StudentServicesStaffTab} from "sections/StudentServices/StudentServicesStaff/StudentServicesStaff"
import {RegistrarStaffTab} from "sections/academics/registrar/RegistrarStaff/RegistrarStaff"
import {ActiveStudentContext, ActiveStudentProvider} from "context/ActiveStudentContext"
import {NewStudentStatusSelect} from "components/NewStudentStatusSelect"
import {UserType} from "types/tasks"
import {RegistrarStudentTab} from "sections/academics/student/AcademicsStudent"
import {useAllDepartmentSubunits} from "hooks"
import {getDefaultOrFirstProfile} from "helpers/task"

type Props = {
    student: Auth.DepartmentStudent | Auth.UserProfile
    departmentId?: number
    isDetailButtonVisible: boolean
    detailUrl?: string
    fromPage?: string
    backUrl?: string
    isHideStudentDetailButton?: boolean
    backCalendarUrl?: string
    advisorTitle?: AdvisorTitle
    isShowHeaderEditIcon?: boolean
    onChangeStudentStatus?: (data: {profileId: number; statusId: number}) => void
    reLoadStudent?: (studentProfileId: number) => void
    renderExtraPhoneEmail?: (props) => JSX.Element
    renderLeftInfo?: (props) => JSX.Element
    renderRightInfo?: (props) => JSX.Element
    onLoadStudentStats?: (studentStats: Auth.StudentStatistics) => void
    fromUserSection?: boolean
    defaultStudentProfileId?: number
}

const studentDetailUrls = {
    academics: {
        goStudentDetailUrl: "/academics/registrar/users/",
        backUrl: `/academics/registrar/staff?tab=${RegistrarStaffTab.Students}`
    },
    admissions: {
        goStudentDetailUrl: "/users/",
        backUrl: "/admissions/staff?tab=2"
    },
    "financial-aid": {
        goStudentDetailUrl: "/users/",
        backUrl: "/financial-aid/staff?tab=students"
    },
    "student-services": {
        goStudentDetailUrl: "/users/",
        backUrl: `/student-services/staff?tab=${StudentServicesStaffTab.Students}`
    },
    "academic-affairs": {
        goStudentDetailUrl: "/users/",
        backUrl: `/academic-affairs/staff?tab=${StudentServicesStaffTab.Students}`
    },
    practical: {
        goStudentDetailUrl: "/users/",
        backUrl: `/academics/practical/`
    }
}

export type DefaultStatus = {
    statusId: number
    name: string
}

export function StudentInfoView(props: Props) {
    const {
        student,
        departmentId,
        detailUrl,
        isDetailButtonVisible,
        isShowHeaderEditIcon,
        onChangeStudentStatus,
        onLoadStudentStats,
        renderExtraPhoneEmail,
        renderLeftInfo,
        renderRightInfo,
        isHideStudentDetailButton,
        defaultStudentProfileId,
        fromUserSection = false
    } = props
    const {setStudent, setDepartmentId, studentAdvisors, advisorsPopup} = useContext(ActiveStudentContext)
    const model = useModel()
    const {isVisible: showEnrollmentId, label: enrollmentIdLabel} = useShowEnrollmentIdField()
    const [holdActivities, setHoldActivities] = useState([])
    const {subunits} = useAllDepartmentSubunits()
    const [isLoading, setIsLoading] = useState(false)
    const [isShowDetail, setIsShowDetail] = useState(false)
    const [studentProfileId, setStudentProfileId] = useState(null)
    const [systemStatus, setSystemStatus] = useState("")
    const [newStudentStatus, setNewStudentStatus] = useState<Partial<StudentStatus>>()

    const [lastDayOfAttendance, setLastDayOfAttendance] = useState("")
    const [studentStats, setStudentStats] = useState(null)
    const [isShowForm, setIsShowForm] = useState<boolean>(false)
    const [userData, setUserData] = useState<Auth.UserProfile>(null)
    const [isLoadingUser, setIsLoadingUser] = useState(false)

    const {t} = useTranslation(["common", "chat", "financialAid", "user"])
    const history = useHistory()
    const isStaff = model.isStaffOrAdmin()
    const defaultFormat = model.getUserDateFormat() || "MM/DD/YYYY"
    const customUserId = get(student, "customUserId")
    const studentProfiles = (userData?.profiles || []).filter(
        (profile) => !!profile.active && profile.state && profile.type === Auth.UserProfileType.Student
    )
    const activeProfile = studentProfiles?.find((profile) => profile.id === studentProfileId)
    const {data: totalScheduleHoursToLda} = useTotalScheduleHoursToLda({
        studentProfileId,
        startDate: activeProfile?.startDate,
        enabled: !!studentProfileId && !!userData
    })
    const resolvedStudentStats = studentStats ? {...studentStats, totalScheduleHoursToLda} : null
    const userId = userData?.id
    const customProfileId = activeProfile?.customProfileId
    const {enrollmentTypes} = useAllEnrollmentTypes()

    useEffect(() => {
        if (studentProfileId) {
            getUserData(studentProfileId)
            getStudentStatuses()
        }
    }, [studentProfileId])

    useEffect(() => {
        if (student) {
            if (defaultStudentProfileId) {
                const studentProfileId = defaultStudentProfileId
                setStudentProfileId(studentProfileId)
                setDepartmentId(departmentId)
                if (studentProfileId && departmentId) {
                    loadWarnings(studentProfileId)
                }
            } else {
                const studentProfileId = !isEmpty(student.profiles)
                    ? getDefaultOrFirstProfile(student as Auth.UserProfile, Auth.UserProfileType.Student).id
                    : +get(student, "profileId")
                setStudentProfileId(studentProfileId)
                setDepartmentId(departmentId)
                if (studentProfileId && departmentId) {
                    loadWarnings(studentProfileId)
                }
            }
        }
    }, [student, departmentId, defaultStudentProfileId])

    useEffect(() => {
        if (studentProfileId && userData) {
            getStudentStatistics(studentProfileId)
        }
    }, [departmentId, studentProfileId, userData])

    const getUserData = async (profileId: number) => {
        try {
            setIsLoadingUser(true)
            const {
                data: [user]
            } = await userServiceV3.getAll({
                filter: {profileId: [profileId]},
                linkedObjects: true
            })
            setUserData(user)

            const {
                data: [student]
            } = await studentService.getDepartmentStudents({
                filter: {profileIds: [profileId], departmentId}
            })
            setStudent(student)
        } catch (error) {
            handleError(error)
        } finally {
            setIsLoadingUser(false)
        }
    }

    const getStudentStatistics = async (studentProfileId) => {
        try {
            const activeProfile = userData?.profiles?.find((profile) => profile.id === studentProfileId)
            const data = await studentService.getOverallStats({studentProfileId})
            const phases = uniqBy(activeProfile?.phases ?? [], "phaseId")
            const shifts = uniqBy(activeProfile?.shifts ?? [], "shiftId")

            setStudentStats({...data, phases, shifts, academicTrack: activeProfile?.academicTrack})

            data.sapStatus = activeProfile?.sapStatus
            data.phaseIds = activeProfile?.defaultPhases.map((item) => item.phaseId)
            data.shiftIds = activeProfile?.defaultShifts.map((item) => item.shiftId)

            const studentLda = data.lastDayOfAttendance
            if (onLoadStudentStats) {
                onLoadStudentStats(data)
            }
            setLastDayOfAttendance(formatDateOnly(studentLda, defaultFormat))
        } catch (error) {
            handleError(error)
        }
    }

    const getStudentStatuses = async () => {
        try {
            if (studentProfileId) {
                setIsLoading(true)
                let systemStatusProfile = ""

                if (student?.profileState) {
                    systemStatusProfile = [Auth.StudentState.Applicant, Auth.StudentState.Prospect].includes(
                        student.profileState
                    )
                        ? student.profileState || "active"
                        : !!student?.profileStatus
                        ? "active"
                        : "inactive"
                }

                if (student?.profiles?.length) {
                    const currentProfile = student.profiles.find((profile) => profile.id === studentProfileId)
                    systemStatusProfile = currentProfile?.active ? "active" : "inactive"

                    setNewStudentStatus({
                        statusId: currentProfile.status?.statusId,
                        name: currentProfile.status?.name,
                        hexColor: currentProfile.status?.hexColor,
                        state: currentProfile.state
                    })
                }
                setSystemStatus(systemStatusProfile)
            }
        } catch (error) {
            handleError(error)
        } finally {
            setIsLoading(false)
        }
    }

    const loadWarnings = async (studentProfileId) => {
        const departmentSubunitIds = subunits
            .filter((subunit) => subunit.departmentId === departmentId)
            .map((subunit) => subunit.subunitId)
        const body = {
            filter: {
                ...(departmentSubunitIds.length && {departmentSubunitIds: departmentSubunitIds}),
                profileId: studentProfileId
            }
        }
        const {data: holdActivities} = await studentActivitiesService.getHoldActivities(body)
        setHoldActivities(holdActivities)
    }

    const checkHasPermissionToUpdateAdvisor = () => {
        if (!departmentId) {
            return isStaff
        }
        switch (departmentId) {
            case BaseDepartmentId.Admissions:
                return checkPermission({staff: [Permissions.Staff.Admissions.Students.UpdateAdvisor.Edit]}, model)
            case BaseDepartmentId.Academics:
                return checkPermission(
                    {staff: [Permissions.Staff.Academics.Registrar.Students.UpdateAdvisor.Edit]},
                    model
                )
            case BaseDepartmentId.FinancialAid:
                return checkPermission({staff: [Permissions.Staff.FinancialAid.Students.UpdateAdvisor.Edit]}, model)
            case BaseDepartmentId.StudentAccount:
                return checkPermission({staff: [Permissions.Staff.StudentAccount.Students.UpdateAdvisor.Edit]}, model)
            case BaseDepartmentId.StudentServices:
                return checkPermission(
                    {
                        staff: [
                            Permissions.Staff.StudentServicesAndAcademicAffairs.StudentServices.Students.UpdateAdvisor
                                .Edit
                        ]
                    },
                    model
                )
            default:
                return false
        }
    }

    const toggleShowDetail = () => {
        setIsShowDetail(!isShowDetail)
    }

    const goDetailView = () => {
        let pushOptions: any = {pathname: `/${detailUrl}/student/${studentProfileId}`, state: null}
        if (props.backUrl) {
            pushOptions = stringifyUrl(
                {
                    url: `/${detailUrl}/student/${studentProfileId}`,
                    query: {
                        tab: RegistrarStudentTab.Workspace,
                        backUrl: props.backUrl,
                        department: departmentId
                    }
                },
                {skipNull: true}
            )
        }
        history.push(pushOptions)
    }

    const goStudentDetail = () => {
        if (userId) {
            let pushOptions = {pathname: `/users/${userId}`, state: null}
            if (props.fromPage) {
                pushOptions = {
                    pathname: `${studentDetailUrls[props.fromPage].goStudentDetailUrl}${userId}`,
                    state: {backUrlFromPage: props.backUrl || studentDetailUrls[props.fromPage].backUrl}
                }
            }
            pushOptions.state = {...pushOptions.state, edit: true, defaultRole: UserType.STUDENT}
            history.push(pushOptions)
        }
    }

    const goCalendar = () => {
        let pushOptions = {pathname: `/calendar`, state: null}
        if (props.backCalendarUrl) {
            pushOptions = {
                pathname: `/academics/registrar/calendar`,
                state: {backUrl: props.backCalendarUrl}
            }
        }
        history.push(pushOptions)
    }

    const goFerpa = () => {
        if (userId) {
            let pushOptions = {pathname: `/users/${userId}`, search: `?tab=${Auth.UserDetailTab.FERPA}`, state: null}
            if (props.fromPage) {
                pushOptions = {
                    pathname: `${studentDetailUrls[props.fromPage].goStudentDetailUrl}${userId}`,
                    search: `?tab=${Auth.UserDetailTab.FERPA}`,
                    state: {backUrlFromPage: props.backUrl || studentDetailUrls[props.fromPage].backUrl}
                }
            }
            pushOptions.state = {...pushOptions.state, edit: true}
            history.push(pushOptions)
        }
    }

    const goDocuments = () => {
        if (userId) {
            let pushOptions = {
                pathname: `/users/${userId}`,
                search: `?tab=${Auth.UserDetailTab.Documents}`,
                state: null
            }
            if (props.fromPage) {
                pushOptions = {
                    pathname: `${studentDetailUrls[props.fromPage].goStudentDetailUrl}${userId}`,
                    search: `?tab=${Auth.UserDetailTab.Documents}`,
                    state: {backUrlFromPage: props.backUrl || studentDetailUrls[props.fromPage].backUrl}
                }
            }
            pushOptions.state = {...pushOptions.state, edit: true}
            history.push(pushOptions)
        }
    }

    const updateNewStudentStatus = async (newStatus: StudentStatus) => {
        await studentService.updateStudentStatus({studentProfileId, statusId: newStatus.statusId})
        onChangeStudentStatus?.({profileId: studentProfileId, statusId: newStatus.statusId})
    }

    const handleNewStudentStatus = (newStatus: StudentStatus) => {
        updateNewStudentStatusMutate.mutate(newStatus)
    }

    const updateNewStudentStatusMutate = useMutation(updateNewStudentStatus, {
        onMutate: (newStatus: StudentStatus) => {
            setNewStudentStatus({
                statusId: newStatus.statusId,
                name: newStatus.name,
                hexColor: newStatus.hexColor,
                state: newStatus.state
            })
            const oldStudentStatus = newStudentStatus
            return {oldStudentStatus}
        },
        onError: (error, newStatus, context) => {
            if (!context) return
            handleError(error)
            setNewStudentStatus({
                statusId: context.oldStudentStatus.statusId,
                name: context.oldStudentStatus.name,
                hexColor: context.oldStudentStatus.hexColor,
                state: context.oldStudentStatus.state
            })
        }
    })

    const renderStudentStatus = () => {
        if (isLoading) {
            return (
                <div className={styles.loadingWrap}>
                    <Spin />
                </div>
            )
        }
        return (
            <>
                <Col span={4}>
                    <div className={styles.statusItem}>
                        <p className={styles.title}>{t("common:message.profileStatus")}</p>
                        <span
                            className={cx(styles.statusBar, {
                                [styles.statusBar__inactive]: ["cancelled", "inactive"].includes(systemStatus)
                            })}>
                            {systemStatus}
                        </span>
                    </div>
                </Col>
                {activeProfile?.state && renderNewStudentStatus()}
            </>
        )
    }

    const renderNewStudentStatus = () => {
        return (
            <NewStudentStatusSelect
                variant="card"
                value={newStudentStatus}
                onChange={handleNewStudentStatus}
                studentState={activeProfile.state as unknown as Auth.StudentState}
            />
        )
    }

    const renderStudentAvatar = () => {
        let studentPhoto = "/image/DefaultAvatar.png"
        if (student?.photo) {
            studentPhoto = student.photo["128"]
        }
        return (
            <div>
                <img src={studentPhoto} className={styles.avatar} alt="" />
            </div>
        )
    }

    const renderHeader = () => {
        const genderPronounValue = genderPronounOptionsKeyById[student?.genderPronoun] || null

        return (
            <>
                <div>
                    {holdActivities.length > 0 &&
                        holdActivities.map(({name}, index) => (
                            <div className={styles.actionNeededWrap} key={index}>
                                <Icon icon="ALERT" className={styles.actionNeededIcon} />
                                <p className={styles.actionNeededText}>{name}</p>
                            </div>
                        ))}
                </div>
                <div className={styles.header}>
                    <div className={styles.leftHeader}>
                        {renderStudentAvatar()}
                        <p className={styles.studentName}>
                            {getFullNameOrPreferredName(student)}
                            {genderPronounValue && (
                                <span className={styles.studentGenderPronoun}>({genderPronounValue?.name})</span>
                            )}
                        </p>
                    </div>
                    <div className={styles.rightHeader}>
                        {isDetailButtonVisible && (
                            <Tooltip title="Detail View">
                                <div className={styles.rightHeaderIconWrap} onClick={goDetailView}>
                                    <Icon icon="DETAIL_FILL_CIRCLE" color="#fff" className={styles.rightHeaderIcon} />
                                </div>
                            </Tooltip>
                        )}
                        <Tooltip title="Student Detail">
                            <div className={styles.rightHeaderIconWrap} onClick={goStudentDetail}>
                                <Icon icon="PROFILE_FILL_CIRCLE" color="#fff" className={styles.rightHeaderIcon} />
                            </div>
                        </Tooltip>
                        <Tooltip title="Appointment">
                            <div className={styles.rightHeaderIconWrap} onClick={goCalendar}>
                                <Icon icon="APPOINTMENT_FILL_CIRCLE" color="#fff" className={styles.rightHeaderIcon} />
                            </div>
                        </Tooltip>
                        <Tooltip title="Documents">
                            <div className={styles.rightHeaderIconWrap} onClick={goDocuments}>
                                <Icon icon="DOCUMENT_FILL_CIRCLE" color="#fff" className={styles.rightHeaderIcon} />
                            </div>
                        </Tooltip>
                        {canShowFerpa && (
                            <Tooltip title="FERPA">
                                <div className={cx(styles.rightHeaderIconWrap, styles.paddingBottom)} onClick={goFerpa}>
                                    <span className={styles.ferpaButton}>FERPA</span>
                                </div>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </>
        )
    }

    const renderPhoneEmailStudent = () => {
        return (
            <>
                <div className={styles.studentPhoneEmail}>
                    <div className={styles.studentPhoneWrap}>
                        <div className={styles.studentPhoneIconWrap}>
                            <Icon icon="PHONE" className={styles.studentPhoneIcon} />
                        </div>
                        <div className={styles.studentPhoneInfo}>
                            {phones.length ? (
                                phones.map((phone, index) => (
                                    <p key={index} className={styles.studentPhone__value}>
                                        {phone}
                                    </p>
                                ))
                            ) : (
                                <p className={styles.studentPhone__value}>N/A</p>
                            )}
                        </div>
                    </div>
                    <div className={styles.studentEmailWrap}>
                        <div className={styles.studentPhoneIconWrap}>
                            <Icon icon="EMAIL" className={styles.studentPhoneIcon} />
                        </div>
                        <div className={styles.studentPhoneInfo}>
                            {emails.length ? (
                                emails.map((email, index) => (
                                    <p
                                        key={index}
                                        className={cx(styles.studentPhone__value, "cursor-copy")}
                                        onClick={() =>
                                            copyTextToClipboard(email).then(() =>
                                                toastInfo(t("common:message.copiedText", {text: email}))
                                            )
                                        }>
                                        {email}
                                    </p>
                                ))
                            ) : (
                                <p className={styles.studentPhone__value}>N/A</p>
                            )}
                        </div>
                    </div>
                </div>
                <div className={styles.studentAddress}>
                    <div className={styles.studentPhoneIconWrap}>
                        <Icon icon="LOCATION_FILL" className={styles.studentPhoneIcon} />
                    </div>
                    <div className={styles.studentPhoneInfo}>
                        <p className={styles.studentPhone__value}>
                            {[student.address, student.city, student.state, student.country, student.postalCode]
                                .map((it) => it?.trim())
                                .filter(Boolean)
                                .join(", ") || "N/A"}
                        </p>
                    </div>
                </div>
            </>
        )
    }

    const renderListAdvisors = () => {
        const studentAdvisorsOrderByDefault = orderBy(studentAdvisors, ["isDefault"], ["desc"])
        return (
            <div className={cx(styles.content__value, styles.advisorsWrap)}>
                {(studentAdvisorsOrderByDefault || []).map((advisor, index) => (
                    <div key={index} className={styles.advisorWrap}>
                        <span className={styles.advisorName}>{getShortName(advisor)}</span>
                        {!!advisor.isDefault && <Icon className={styles.defaultIcon} icon="CHECKMARK_CIRCLE" />}
                        {index !== studentAdvisorsOrderByDefault.length - 1 && ", "}
                    </div>
                ))}
            </div>
        )
    }

    const getDepartmentAdvisorTitle = () => {
        switch (departmentId) {
            case BaseDepartmentId.Admissions:
                return t("studentInfo.advisor", {departmentName: "Admissions"})
            case BaseDepartmentId.FinancialAid:
                return t("studentInfo.advisor", {departmentName: "Financial Aid"})
            case BaseDepartmentId.StudentAccount:
                return t("studentInfo.advisor", {departmentName: "Student Account"})
            case BaseDepartmentId.StudentServices:
                return t("studentInfo.advisor", {departmentName: "Student Service"})
            case BaseDepartmentId.Academics:
                return t("studentInfo.advisor", {departmentName: "Academic"})
            case BaseDepartmentId.CareerServices:
                return t("studentInfo.advisor", {departmentName: "Career Services"})
            default:
                return t("studentInfo.advisor", {departmentName: ""})
        }
    }

    const checkEditStudentPermission = () => {
        switch (departmentId) {
            case BaseDepartmentId.Admissions:
                return checkPermission(
                    {staff: [Permissions.Staff.Admissions.Students.AdmissionsStudentDetails.Edit]},
                    model
                )
            case BaseDepartmentId.FinancialAid:
                return checkPermission(
                    {staff: [Permissions.Staff.FinancialAid.Students.FinancialAidStudentDetail.Edit]},
                    model
                )
            case BaseDepartmentId.Academics:
                return checkPermission(
                    {staff: [Permissions.Staff.AcademicAffairs.StudentServices.Students.StudentServices.Edit]},
                    model
                )
            case BaseDepartmentId.StudentServices:
                return checkPermission(
                    {staff: [Permissions.Staff.AcademicAffairs.StudentServices.Students.AcademicPlanning.Edit]},
                    model
                )
            default:
                return false
        }
    }

    const onSaveSuccess = () => {
        setIsShowForm(false)
        getUserData(studentProfileId)
    }

    const renderFormBody = () => {
        switch (departmentId) {
            case BaseDepartmentId.Admissions:
                return <AdmissionsForm onClickCancel={() => setIsShowForm(false)} onSaveSuccess={onSaveSuccess} />
            case BaseDepartmentId.FinancialAid:
                return <FinancialAidForm onClickCancel={() => setIsShowForm(false)} onSaveSuccess={onSaveSuccess} />
            case BaseDepartmentId.Academics:
                return <AcademicsForm onClickCancel={() => setIsShowForm(false)} onSaveSuccess={onSaveSuccess} />
            case BaseDepartmentId.StudentServices:
                return <StudentServicesForm onClickCancel={() => setIsShowForm(false)} onSaveSuccess={onSaveSuccess} />
            default:
                return null
        }
    }

    if (isLoadingUser || !userData) {
        return (
            <div className={styles.loadingWrap}>
                <BaseLoading isShow />
            </div>
        )
    }

    if (!student) {
        return <div>Not found</div>
    }

    const startDate = activeProfile?.startDate ? moment(activeProfile.startDate).utc().format("MM/DD/YYYY") : null
    const academicTrack = activeProfile?.academicTrack || "-"
    const enrollmentType = (enrollmentTypes || []).find(({code}) => code === activeProfile?.enrollmentType)
    const enrollmentStatus = enrollmentType ? enrollmentType.name : ""
    let campuses = uniq(activeProfile?.campuses.map((campus) => campus.name).filter((item) => item))
    const termName = get(activeProfile, "currentTerm.name", "")
    const programName = get(activeProfile, "currentMajor.name", "")
    const contractEndDate = activeProfile?.contractEndDate
        ? moment(activeProfile.contractEndDate).utc().format("MM/DD/YYYY")
        : ""
    const firstDateOfClass = activeProfile?.firstDateOfClass
        ? moment(activeProfile.firstDateOfClass).utc().format("MM/DD/YYYY")
        : ""
    const projectedEndDate = activeProfile?.projectedEndDate
        ? moment(activeProfile.projectedEndDate).utc().format("MM/DD/YYYY")
        : ""
    const naccasGradDate = activeProfile?.naccasGradDate
        ? moment(activeProfile.naccasGradDate).utc().format("MM/DD/YYYY")
        : ""
    const actualEndDate = activeProfile?.actualEndDate
        ? moment(activeProfile.actualEndDate).utc().format("MM/DD/YYYY")
        : ""
    const state = activeProfile?.state as any
    const temporaryPermanentOutDate = [
        Auth.StudentState.TemporaryOut,
        Auth.StudentState.PermanentOut,
        Auth.StudentState.NeverAttended
    ].includes(state)
        ? moment(
              state === Auth.StudentState.TemporaryOut
                  ? activeProfile.temporaryOutEffectiveDate
                  : activeProfile.cancelDate ?? activeProfile.withdrawalDate
          )
              .utc()
              .format("MM/DD/YYYY")
        : ""

    const startingTermName = get(activeProfile, "startingTerm.name", "")
    const versionName = get(activeProfile, "currentMajorVersionId.versionName", "")
    const versionCode = get(activeProfile, "currentMajorVersionId.versionCode", "")
    const programVersionName = `${versionName}${versionCode ? ` (${versionCode})` : ""}`
    const phones = userData.phones.map(({phone}) => phone)
    const emails = userData.emails.map(({email}) => email)
    const hasPermissionToUpdateAdvisor = checkHasPermissionToUpdateAdvisor()
    const canShowFerpa = checkPermission(
        {
            staff: [Permissions.Staff.Users.Students.Ferpa.View]
        },
        model
    )
    if (campuses.length > 6) {
        const lastString = `+${campuses.slice(5).length}`
        campuses = campuses.slice(0, 5)
        campuses[campuses.length - 1] = `${
            campuses[campuses.length - 1]
        } <span style="color: var(--primary-400-base)"> (${lastString})</span>`
    }

    const renderListCustomProfileIds = () => {
        return (
            <Menu className={styles.menuActionWrap}>
                {studentProfiles.map((profile, index) => (
                    <Menu.Item
                        key={index}
                        className={styles.menuActionItem}
                        onClick={() => setStudentProfileId(profile.id)}>
                        <span>
                            {profile.customProfileId} (
                            {[Auth.UserProfileState.Applicant, Auth.UserProfileState.Prospect].includes(profile.state)
                                ? t("studentInfo.applicantID")
                                : [Auth.UserProfileState.Student, Auth.UserProfileState.Alumni].includes(profile?.state)
                                ? enrollmentIdLabel
                                : t("studentInfo.profileId")}
                            )
                        </span>
                    </Menu.Item>
                ))}
            </Menu>
        )
    }

    const isProspectOrApplicant = [Auth.UserProfileState.Applicant, Auth.UserProfileState.Prospect].includes(
        activeProfile?.state
    )

    const isStudentOrAlumni = [Auth.UserProfileState.Student, Auth.UserProfileState.Alumni].includes(
        activeProfile?.state
    )

    const customProfileIdLabel = isProspectOrApplicant
        ? t("studentInfo.applicantID")
        : isStudentOrAlumni
        ? enrollmentIdLabel
        : t("studentInfo.profileId")

    const showCustomProfileId = !isStudentOrAlumni || showEnrollmentId

    if (isShowForm) return renderFormBody()

    const isShowProjectedEndDate =
        checkPermission({student: [Permissions.Student.Users.AcademicProgress.ProjectedGradDate.View]}, model, true) &&
        isFieldActive(model, Settings.GeneralLabel.ProjectedEndDate)
    const isShowNaccasGradDate =
        checkPermission({student: [Permissions.Student.Users.AcademicProgress.NacasGradDate.View]}, model, true) &&
        isFieldActive(model, Settings.GeneralLabel.NaccasGradDate)

    return (
        <div className={styles.userWrap}>
            {isShowHeaderEditIcon && checkEditStudentPermission() ? (
                <Button
                    type="primary"
                    shape="circle"
                    icon={<Icon icon="EDIT_LINE" color="#FFF" />}
                    className={styles.headerEditIconWrap}
                    onClick={() => setIsShowForm(true)}
                />
            ) : (
                ""
            )}
            <div className={styles.statusWrap}>
                <div className={styles.statusContainer}>{renderStudentStatus()}</div>
                <div className={styles.hideDetailBtn} onClick={toggleShowDetail}>
                    <span className={styles.hideDetail__text}>
                        {isShowDetail ? t("studentInfo.hideDetails") : t("studentInfo.moreDetails")}
                    </span>
                    <div
                        className={cx(styles.hideDetailIconWrap, {
                            [styles.hideDetailIcon__collapse]: isShowDetail
                        })}>
                        <Icon icon="CHEVRON_DOWN" color="var(--primary-400-base)" className={styles.hideDetailIcon} />
                    </div>
                </div>
            </div>
            <Row gutter={[24, 10]}>
                {isHideStudentDetailButton ? null : <Col span={24}>{renderHeader()}</Col>}
                <Col span={24}>
                    <div className={styles.body}>
                        <div className={styles.content}>
                            {showCustomProfileId && (
                                <div className={styles.contentItem}>
                                    <span className={styles.content__title}>{customProfileIdLabel}</span>
                                    {studentProfiles?.length > 1 ? (
                                        <Dropdown overlay={renderListCustomProfileIds} trigger={["click"]}>
                                            <div className={styles.enrollmentWrap}>
                                                <span className={cx(styles.content__value, styles.enrollmentId)}>
                                                    {activeProfile?.customProfileId}
                                                </span>
                                                <Icon icon="ARROW_DOWN" className={styles.arrowDownIcon} />
                                            </div>
                                        </Dropdown>
                                    ) : (
                                        <span className={styles.content__value}>{customProfileId}</span>
                                    )}
                                </div>
                            )}
                            <div className={styles.contentItem}>
                                <span className={styles.content__title}>{getDepartmentAdvisorTitle()}</span>
                                {hasPermissionToUpdateAdvisor ? (
                                    <div
                                        className={cx(styles.content__value, styles.selectAdvisor)}
                                        onClick={advisorsPopup.open}>
                                        {studentAdvisors && studentAdvisors.length ? (
                                            renderListAdvisors()
                                        ) : (
                                            <p className={styles.selectAdvisor__text}>
                                                {departmentId
                                                    ? t("studentInfo.selectAdvisor")
                                                    : t("studentInfo.showAdvisor")}
                                            </p>
                                        )}
                                    </div>
                                ) : (
                                    renderListAdvisors()
                                )}
                            </div>
                            <div className={styles.contentItem}>
                                <span className={styles.content__title}>
                                    {getFieldLabel(model, Settings.GeneralLabel.Program, t("studentInfo.program"))}
                                </span>
                                <span className={styles.content__value}>{programName}</span>
                            </div>
                        </div>
                        <div className={styles.content}>
                            <div className={styles.contentItem}>
                                <span className={styles.content__title}>{t("studentInfo.userID")}</span>
                                <span className={styles.content__value}>{customUserId}</span>
                            </div>
                            <div className={styles.contentItem}>
                                <span className={styles.content__title}>{t("studentInfo.enrollmentType")}</span>
                                <span className={styles.content__value}>{enrollmentStatus}</span>
                            </div>
                            <div className={styles.contentItem}>
                                <span className={styles.content__title}>
                                    {getFieldLabel(
                                        model,
                                        Settings.GeneralLabel.ProgramVersion,
                                        t("studentInfo.programVersion")
                                    )}
                                </span>
                                <span className={styles.content__value}>{programVersionName}</span>
                            </div>
                        </div>
                        <div className={styles.content}>
                            <div className={styles.contentItem}>
                                <span className={styles.content__title}>{t("user:user.startTerm")}</span>
                                <span className={styles.content__value}>{startingTermName}</span>
                            </div>
                            <div className={styles.contentItem}>
                                <span className={styles.content__title}>
                                    {getFieldLabel(model, Settings.GeneralLabel.Campus, t("studentInfo.campus"))}
                                </span>
                                <div className={cx(styles.content__value, styles.campusWrap)}>
                                    {campuses.map((campus, index) => (
                                        <span key={index} className={styles.campusName}>
                                            <div dangerouslySetInnerHTML={{__html: campus}} />
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div className={styles.contentItem}>
                                <span className={styles.content__title}>
                                    {getFieldLabel(
                                        model,
                                        Settings.GeneralLabel.AcademicTrack,
                                        t("studentInfo.academicTrack")
                                    )}
                                </span>
                                <span className={styles.content__value}>{academicTrack}</span>
                            </div>
                        </div>
                        <div className={styles.content}>
                            <div className={styles.contentItem}>
                                <span className={styles.content__title}>
                                    {getFieldLabel(model, Settings.GeneralLabel.StartDate, t("studentInfo.startDate"))}
                                </span>
                                <span className={styles.content__value}>{startDate}</span>
                            </div>
                            <div className={styles.contentItem}>
                                <span className={styles.content__title}>
                                    {getFieldLabel(
                                        model,
                                        Settings.GeneralLabel.ContractEndDate,
                                        t("studentInfo.contractEndDate")
                                    )}
                                </span>
                                <span className={styles.content__value}>{contractEndDate}</span>
                            </div>
                            <div className={styles.contentItem}>
                                <span className={styles.content__title}>
                                    {getFieldLabel(
                                        model,
                                        Settings.GeneralLabel.CurrentTerm,
                                        t("studentInfo.currentTerm")
                                    )}
                                </span>
                                <span className={styles.content__value}>{termName}</span>
                            </div>
                        </div>
                        <div className={styles.content}>
                            <div className={styles.contentItem}>
                                <span className={styles.content__title}>
                                    {getFieldLabel(
                                        model,
                                        Settings.GeneralLabel.FirstDateOfClass,
                                        t("studentInfo.firstDateOfClass")
                                    )}
                                </span>
                                <span className={styles.content__value}>{firstDateOfClass}</span>
                            </div>
                            {isShowProjectedEndDate && (
                                <div className={styles.contentItem}>
                                    <span className={styles.content__title}>
                                        {getFieldLabel(
                                            model,
                                            Settings.GeneralLabel.ProjectedEndDate,
                                            t("studentInfo.projectedEndDate")
                                        )}
                                    </span>
                                    <span className={styles.content__value}>{projectedEndDate}</span>
                                </div>
                            )}
                            <div className={styles.contentItem}>
                                <span className={styles.content__title}>
                                    {t("studentInfo.temporaryOutPermanentOutDate")}
                                </span>
                                <span className={styles.content__value}>{temporaryPermanentOutDate}</span>
                            </div>
                            {!isShowProjectedEndDate && (
                                <div className={cx(styles.contentItem, styles.contentItemNoBorder)} />
                            )}
                        </div>
                        <div className={styles.content}>
                            <div className={styles.contentItem}>
                                <span className={styles.content__title}>
                                    {getFieldLabel(
                                        model,
                                        Settings.GeneralLabel.ActualEndDate,
                                        t("studentInfo.actualGraduationDate")
                                    )}
                                </span>
                                <span className={styles.content__value}>{actualEndDate}</span>
                            </div>
                            {isShowNaccasGradDate && (
                                <div className={styles.contentItem}>
                                    <span className={styles.content__title}>
                                        {getFieldLabel(
                                            model,
                                            Settings.GeneralLabel.NaccasGradDate,
                                            t("studentInfo.naccasGradDate")
                                        )}
                                    </span>
                                    <span className={styles.content__value}>{naccasGradDate}</span>
                                </div>
                            )}
                            <div className={styles.contentItem}>
                                <span className={styles.content__title}>
                                    {getFieldLabel(model, Settings.GeneralLabel.Lda, t("user.lastDayAttendance"))}
                                </span>
                                <span className={styles.content__value}>{lastDayOfAttendance}</span>
                            </div>
                            {!isShowNaccasGradDate && (
                                <div className={cx(styles.contentItem, styles.contentItemNoBorder)} />
                            )}
                        </div>
                        <div className={styles.content}></div>
                        {isShowDetail && (
                            <div className={styles.detailBody}>
                                {renderLeftInfo ? (
                                    renderLeftInfo({
                                        student,
                                        departmentId,
                                        studentStats: resolvedStudentStats,
                                        activeProfileId: studentProfileId
                                    })
                                ) : (
                                    <div className={styles.detailBodyLeft}>
                                        <div style={{display: "flex", flexDirection: "column", gap: 8, padding: 10}}>
                                            {renderPhoneEmailStudent()}
                                        </div>
                                        {renderExtraPhoneEmail &&
                                            renderExtraPhoneEmail({
                                                student,
                                                departmentId,
                                                studentStats: resolvedStudentStats,
                                                activeProfileId: studentProfileId
                                            })}
                                    </div>
                                )}
                                {renderRightInfo ? (
                                    renderRightInfo({
                                        student,
                                        departmentId,
                                        studentStats: resolvedStudentStats,
                                        activeProfileId: studentProfileId,
                                        lastDayOfAttendance
                                    })
                                ) : (
                                    <div className={cx(styles.detailBodyRight, styles.detailBodyRight__empty)} />
                                )}
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        </div>
    )
}

export function StudentInfo(props: Props) {
    return (
        <ActiveStudentProvider departmentId={props.departmentId}>
            <StudentInfoView {...props} />
        </ActiveStudentProvider>
    )
}
